import {
  ILayerPerformanceCriteriaImperial,
  ILayerPerformanceCriteriaMetric,
  IPerRoadLayerImperial,
  IPerRoadLayerMetric,
  IPerRoadScenarioImperial,
  IPerRoadScenarioMetric,
  IPerRoadSeasonImperial,
  IPerRoadSeasonMetric,
} from "@/modules/perRoad/interfaces/perRoad";
import {UnitSystem} from "@/interfaces/units";
import {ImperialUnits, MetricUnits} from "@/lib/unitConversion/unitConversionFactors";
import convert from "convert-units";
import {
  convertDistributionImperialToMetric,
  convertDistributionMetricToImperial,
} from "@/lib/unitConversion/convertDistribution";
import {IPrTireLoadImperial, IPrTireLoadMetric} from "@/modules/perRoad/interfaces/monteCarlo";

/**
 * Loads
 */

export const perRoadTireLoadImperialToMetric = (A: IPrTireLoadImperial): IPrTireLoadMetric => {
  const B: IPrTireLoadMetric = {
    unitSystem: UnitSystem.Metric,
    criticalLayout_cm: A.criticalLayout_in.map((xy) => {
      return {
        x: convert(xy.x).from(ImperialUnits.LENGTH_IN).to(MetricUnits.LENGTH_CM),
        y: convert(xy.y).from(ImperialUnits.LENGTH_IN).to(MetricUnits.LENGTH_CM),
      };
    }),
    load_kg: convert(A.load_lbf).from(ImperialUnits.MASS_LB).to(MetricUnits.MASS_KG),
    location_cm: {
      x: convert(A.location_in.x).from(ImperialUnits.LENGTH_IN).to(MetricUnits.LENGTH_CM),
      y: convert(A.location_in.y).from(ImperialUnits.LENGTH_IN).to(MetricUnits.LENGTH_CM),
    },
    tirePressure_mpa: convert(A.tirePressure_psi).from(ImperialUnits.PRESSURE_PSI).to(MetricUnits.PRESSURE_MPA)
  };

  return B;
};

export const perRoadTireLoadMetricToImperial = (A: IPrTireLoadMetric): IPrTireLoadImperial=> {
  const B: IPrTireLoadImperial = {
    unitSystem: UnitSystem.Imperial,
    criticalLayout_in: A.criticalLayout_cm.map((xy) => {
      return {
        x: convert(xy.x).from(MetricUnits.LENGTH_CM).to(ImperialUnits.LENGTH_IN),
        y: convert(xy.y).from(MetricUnits.LENGTH_CM).to(ImperialUnits.LENGTH_IN),
      };
    }),
    load_lbf: convert(A.load_kg).from(ImperialUnits.MASS_LB).to(MetricUnits.MASS_KG),
    location_in: {
      x: convert(A.location_cm.x).from(MetricUnits.LENGTH_CM).to(ImperialUnits.LENGTH_IN),
      y: convert(A.location_cm.y).from(MetricUnits.LENGTH_CM).to(ImperialUnits.LENGTH_IN),
    },
    tirePressure_psi: convert(A.tirePressure_mpa).from(MetricUnits.PRESSURE_MPA).to(MetricUnits.PRESSURE_MPA)
  };

  return B;
};

/**
 * Performance Criteria
 */

const perRoadLayerPerformanceImperialToMetric = (A: ILayerPerformanceCriteriaImperial): ILayerPerformanceCriteriaMetric => {
  const B: ILayerPerformanceCriteriaMetric = {
    id: A.id,
    unitSystem: UnitSystem.Metric,
    enabled: A.enabled,
    criteria: A.criteria,
    targetPercentile: A.targetPercentile,
    strainThreshold_ustrain: A.strainThreshold_ustrain,
    distributionPercentiles: A.distributionPercentiles,
    distribution_ustrain: A.distribution_ustrain,
    useTransferFunction: A.useTransferFunction,
    location: A.location,
    k1: A.k1,
    k2: A.k2,


    deflectionThreshold_cm: convert(A.deflectionThreshold_in || 0).from(ImperialUnits.LENGTH_IN).to(MetricUnits.LENGTH_CM),
    stressThreshold_mpa: convert(A.stressThreshold_psi || 0).from(ImperialUnits.PRESSURE_PSI).to(MetricUnits.PRESSURE_MPA),

  };

  return B;
};

const perRoadLayerPerformanceMetricToImperial = (A: ILayerPerformanceCriteriaMetric): ILayerPerformanceCriteriaImperial => {
  const B: ILayerPerformanceCriteriaImperial = {
    id: A.id,
    unitSystem: UnitSystem.Imperial,
    enabled: A.enabled,
    criteria: A.criteria,
    targetPercentile: A.targetPercentile,
    strainThreshold_ustrain: A.strainThreshold_ustrain,
    distributionPercentiles: A.distributionPercentiles,
    distribution_ustrain: A.distribution_ustrain,
    useTransferFunction: A.useTransferFunction,
    location: A.location,
    k1: A.k1,
    k2: A.k2,

    deflectionThreshold_in: convert(A.deflectionThreshold_cm || 0).from(MetricUnits.LENGTH_CM).to(ImperialUnits.LENGTH_IN),
    stressThreshold_psi: convert(A.stressThreshold_mpa || 0).from(MetricUnits.PRESSURE_MPA).to(ImperialUnits.PRESSURE_PSI),
  };
  return B;
};


/**
 * Layers
 *
 * @param A
 */
export const perRoadLayerImperialToMetric = (A: IPerRoadLayerImperial): IPerRoadLayerMetric => {
  const B: IPerRoadLayerMetric = {
    id: A.id,
    name: A.name,
    type: A.type,
    enabled: A.enabled,
    unitSystem: UnitSystem.Metric,

    // Degrees celsius always, increments of 8degC
    pgGradeHigh: A.pgGradeHigh,
    pgGradeLow: A.pgGradeLow,

    poissonRatio: A.poissonRatio,
    performanceCriteria: A.performanceCriteria.map((c) => perRoadLayerPerformanceImperialToMetric(c)),

    modulus_mpa: A.modulus_psi.map((d) => convertDistributionImperialToMetric(
      d,
      ImperialUnits.PRESSURE_PSI,
      MetricUnits.PRESSURE_MPA,
    )),
    thickness_cm: convertDistributionImperialToMetric(A.thickness_in, ImperialUnits.LENGTH_IN, MetricUnits.LENGTH_CM),

  };
  return B;
};

export const perRoadLayerMetricToImperial = (A: IPerRoadLayerMetric): IPerRoadLayerImperial => {
  const B: IPerRoadLayerImperial = {
    id: A.id,
    name: A.name,
    type: A.type,
    enabled: A.enabled,
    unitSystem: UnitSystem.Imperial,

    // Degrees celsius always, increments of 8degC
    pgGradeHigh: A.pgGradeHigh,
    pgGradeLow: A.pgGradeLow,

    poissonRatio: A.poissonRatio,
    performanceCriteria: A.performanceCriteria.map((c) => perRoadLayerPerformanceMetricToImperial(c)),

    modulus_psi: A.modulus_mpa.map((d) => convertDistributionMetricToImperial(
      d,
      MetricUnits.PRESSURE_MPA,
      ImperialUnits.PRESSURE_PSI,
    )),
    thickness_in: convertDistributionMetricToImperial(A.thickness_cm, MetricUnits.LENGTH_CM, ImperialUnits.LENGTH_IN),
  };
  return B;
};

/**
 * Seasons
 * @param A
 */
export const perRoadSeasonImperialToMetric = (A: IPerRoadSeasonImperial): IPerRoadSeasonMetric => {
  const B: IPerRoadSeasonMetric = {
    id: A.id,
    name: A.name,
    duration_weeks: A.duration_weeks,
    enabled: A.enabled,

    // Conversions
    temp_degC: convert(A.temp_degF).from(ImperialUnits.TEMP_F).to(MetricUnits.TEMP_C),
    unitSystem: UnitSystem.Metric,
  };
  return B;
};

export const perRoadSeasonMetricToImperial = (A: IPerRoadSeasonMetric): IPerRoadSeasonImperial => {
  const B: IPerRoadSeasonImperial = {
    id: A.id,
    name: A.name,
    duration_weeks: A.duration_weeks,
    enabled: A.enabled,

    // Conversions
    temp_degF: convert(A.temp_degC).from(MetricUnits.TEMP_C).to(ImperialUnits.TEMP_F),
    unitSystem: UnitSystem.Imperial,
  };
  return B;
};

/**
 * Scenarios
 * @param A
 */

export const perRoadScenarioImperialToMetric = (A: IPerRoadScenarioImperial): IPerRoadScenarioMetric => {
  const B: IPerRoadScenarioMetric = {
    id: A.id,
    user: A.user,
    name: A.name,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: A.displayUnits,
    unitSystem: UnitSystem.Metric,
    scenarioType: A.scenarioType,
    isComputedFromLegacy: A.isComputedFromLegacy,

    district: A.district,
    county: A.county,
    route: A.route,
    description: A.description,

    /*
     Loading Conditions
     */

    twoWayAadt: A.twoWayAadt,
    axleGroupsPerDay: A.axleGroupsPerDay,
    pctTrucks: A.pctTrucks,
    pctTruckGrowthPerYear: A.pctTruckGrowthPerYear,
    pctTrucksInDesignLane: A.pctTrucksInDesignLane,
    directionalDistribution: A.directionalDistribution,

    // Assumes one entry in each of these variables for each vehicle classification from 4 thru 13
    vehiclesPctAadt: A.vehiclesPctAadt,
    vehicleAxlesSingle: A.vehicleAxlesSingle,
    vehicleAxlesTandem: A.vehicleAxlesTandem,
    vehicleAxlesTridem: A.vehicleAxlesTridem,
    vehicleAxlesSteer: A.vehicleAxlesSteer,
    roadwayClassification: A.roadwayClassification,

    // For storing custom loading distributions
    axleLoadDistSingle: A.axleLoadDistSingle,
    axleLoadDistTandem: A.axleLoadDistTandem,
    axleLoadDistTridem: A.axleLoadDistTridem,

    axleSingleEnabled: A.axleSingleEnabled,
    axleTandemEnabled: A.axleTandemEnabled,
    axleTridemEnabled: A.axleTridemEnabled,
    axleSteerEnabled: A.axleSteerEnabled,

    /**
     * Actual conversions
     */

    layers: A.layers.map((L) => perRoadLayerImperialToMetric(L)),
    seasons: A.seasons.map((S) => perRoadSeasonImperialToMetric(S)),

  };

  return B;
};


export const perRoadScenarioMetricToImperial = (A: IPerRoadScenarioMetric): IPerRoadScenarioImperial => {
  const B: IPerRoadScenarioImperial = {
    id: A.id,
    user: A.user,
    name: A.name,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: A.displayUnits,
    unitSystem: UnitSystem.Imperial,
    scenarioType: A.scenarioType,
    isComputedFromLegacy: A.isComputedFromLegacy,

    district: A.district,
    county: A.county,
    route: A.route,
    description: A.description,

    /*
     Loading Conditions
     */

    twoWayAadt: A.twoWayAadt,
    axleGroupsPerDay: A.axleGroupsPerDay,
    pctTrucks: A.pctTrucks,
    pctTruckGrowthPerYear: A.pctTruckGrowthPerYear,
    pctTrucksInDesignLane: A.pctTrucksInDesignLane,
    directionalDistribution: A.directionalDistribution,

    // Assumes one entry in each of these variables for each vehicle classification from 4 thru 13
    vehiclesPctAadt: A.vehiclesPctAadt,
    vehicleAxlesSingle: A.vehicleAxlesSingle,
    vehicleAxlesTandem: A.vehicleAxlesTandem,
    vehicleAxlesTridem: A.vehicleAxlesTridem,
    vehicleAxlesSteer: A.vehicleAxlesSteer,
    roadwayClassification: A.roadwayClassification,

    // For storing custom loading distributions
    axleLoadDistSingle: A.axleLoadDistSingle,
    axleLoadDistTandem: A.axleLoadDistTandem,
    axleLoadDistTridem: A.axleLoadDistTridem,

    axleSingleEnabled: A.axleSingleEnabled,
    axleTandemEnabled: A.axleTandemEnabled,
    axleTridemEnabled: A.axleTridemEnabled,
    axleSteerEnabled: A.axleSteerEnabled,

    /**
     * Actual conversions
     */

    layers: A.layers.map((L) => perRoadLayerMetricToImperial(L)),
    seasons: A.seasons.map((S) => perRoadSeasonMetricToImperial(S)),

  };

  return B;
};
